import { onCleanup, onMount, reactive, watch } from 'cdui-js';
import { bankVerifyText } from './verifyCopy';

const THUMB_WIDTH = 72;

export type BankSliderCaptcha = {
  shadeImage?: string;
  cutoutImage?: string;
  cutoutWidth?: number;
  cutoutHeight?: number;
  y?: number;
  width?: number;
  height?: number;
  validateCode?: string;
};

export type CaptchaPhase = 'idle' | 'checking' | 'ok' | 'bad' | 'refreshing';

const positive = (value: unknown) => {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

export const isBankSliderCaptcha = (value: unknown): value is BankSliderCaptcha => {
  if (!value || typeof value !== 'object') return false;
  const row = value as BankSliderCaptcha;
  return Boolean(
    String(row.validateCode || '').trim() &&
      String(row.shadeImage || '').trim() &&
      String(row.cutoutImage || '').trim() &&
      positive(row.width) &&
      positive(row.height) &&
      positive(row.cutoutWidth) &&
      positive(row.cutoutHeight),
  );
};

const toImageUri = (raw?: string) => {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (text.startsWith('data:')) return text;
  const body = text.replace(/\s+/g, '');
  if (body.startsWith('iVBORw0KGgo')) return `data:image/png;base64,${body}`;
  if (body.startsWith('/9j/')) return `data:image/jpeg;base64,${body}`;
  return '';
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type VerifyCaptchaProps = {
  captcha: BankSliderCaptcha;
  phase: CaptchaPhase;
  statusText: string;
  brokenText: string;
  onClose: () => void;
  onRefresh: () => void;
  onSolved: (slideX: number) => void;
};

export const VerifyCaptcha = (props: VerifyCaptchaProps) => {
  let stageEl: HTMLDivElement | null = null;
  let trackEl: HTMLDivElement | null = null;
  let dragStartX = 0;
  let dragStartThumb = 0;
  let pointerId: number | null = null;
  let alive = true;

  const state = reactive({
    thumbX: 0,
    stageWidth: 0,
    trackWidth: 0,
    imageError: false,
    dragging: false,
  });

  const text = () => bankVerifyText();
  const locked = () => props.phase === 'checking' || props.phase === 'ok' || props.phase === 'refreshing';
  const shadeSrc = () => toImageUri(props.captcha.shadeImage);
  const pieceSrc = () => toImageUri(props.captcha.cutoutImage);
  const broken = () => props.brokenText || (state.imageError ? text().loadFail : '');

  const scale = () => {
    const sourceWidth = positive(props.captcha.width);
    if (!sourceWidth || !state.stageWidth) return 0;
    return state.stageWidth / sourceWidth;
  };
  const stageHeight = () => {
    const ratio = scale();
    const sourceHeight = positive(props.captcha.height);
    return ratio > 0 && sourceHeight ? sourceHeight * ratio : 120;
  };
  const pieceWidth = () => positive(props.captcha.cutoutWidth) * scale();
  const pieceHeight = () => positive(props.captcha.cutoutHeight) * scale();
  const pieceTop = () => Math.max(0, Number(props.captcha.y) || 0) * scale();
  const thumbMax = () => Math.max(0, state.trackWidth - THUMB_WIDTH);
  const pieceMax = () => Math.max(0, state.stageWidth - pieceWidth());
  const pieceX = () => {
    const max = thumbMax();
    if (max <= 0) return 0;
    return (state.thumbX / max) * pieceMax();
  };

  const measure = (resetMotion = false) => {
    if (!alive) return;
    if (!stageEl || !trackEl) {
      requestAnimationFrame(() => measure(resetMotion));
      return;
    }
    state.stageWidth = stageEl.clientWidth || 0;
    state.trackWidth = trackEl.clientWidth || 0;
    const max = Math.max(0, state.trackWidth - THUMB_WIDTH);
    state.thumbX = resetMotion ? 0 : clamp(state.thumbX, 0, max);
    if (!shadeSrc() || !pieceSrc()) state.imageError = true;
  };

  const slideX = () => {
    const ratio = scale();
    if (ratio <= 0) return 0;
    return Math.max(0, Math.round(pieceX() / ratio));
  };

  const canDrag = () => props.phase === 'idle' && !broken();
  const canRefresh = () => props.phase === 'idle';

  const onPointerDown = (event: PointerEvent) => {
    if (!canDrag()) return;
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture?.(event.pointerId);
    pointerId = event.pointerId;
    state.dragging = true;
    dragStartX = event.clientX;
    dragStartThumb = state.thumbX;
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!state.dragging || (pointerId != null && event.pointerId !== pointerId)) return;
    state.thumbX = clamp(dragStartThumb + (event.clientX - dragStartX), 0, thumbMax());
  };

  const onPointerUp = (event: PointerEvent) => {
    if (!state.dragging || (pointerId != null && event.pointerId !== pointerId)) return;
    state.dragging = false;
    pointerId = null;
    if (!canDrag()) return;
    props.onSolved(slideX());
  };

  const close = () => {
    if (locked()) return;
    props.onClose();
  };

  watch(
    () => props.captcha.validateCode,
    () => {
      state.imageError = false;
      measure(true);
    },
  );

  onMount(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    measure(true);
    onCleanup(() => {
      alive = false;
      window.removeEventListener('resize', onResize);
    });
  });

  const statusClass = () => {
    if (props.phase === 'checking') return ' is-checking';
    if (props.phase === 'bad') return ' is-bad';
    if (props.phase === 'ok') return ' is-ok';
    return '';
  };

  return (
    <div
      class="bank-verify-captcha-mask"
      onclick={(event) => {
        event.stopPropagation?.();
        close();
      }}
    >
      <div
        class="bank-verify-captcha-card"
        role="dialog"
        aria-modal="true"
        onclick={(event) => event.stopPropagation?.()}
      >
        <div class="bank-verify-captcha-hint">{text().puzzleHint}</div>
        <div
          class="bank-verify-captcha-stage"
          ref={(el: HTMLDivElement) => {
            stageEl = el;
          }}
          style={{ height: `${stageHeight()}px` }}
        >
          {broken() ? (
            <div class="bank-verify-captcha-broken">{broken()}</div>
          ) : (
            <>
              <img
                class="bank-verify-captcha-shade"
                src={shadeSrc()}
                alt=""
                draggable={false}
                onerror={() => {
                  state.imageError = true;
                }}
              />
              <img
                class="bank-verify-captcha-piece"
                src={pieceSrc()}
                alt=""
                draggable={false}
                style={{
                  width: `${pieceWidth()}px`,
                  height: `${pieceHeight()}px`,
                  top: `${pieceTop()}px`,
                  transform: `translateX(${pieceX()}px)`,
                }}
                onerror={() => {
                  state.imageError = true;
                }}
              />
            </>
          )}
          {props.phase === 'checking' || props.phase === 'bad' || props.phase === 'ok' ? (
            <div class={`bank-verify-captcha-status${statusClass()}`}>{props.statusText}</div>
          ) : null}
        </div>
        <div
          class="bank-verify-captcha-track-wrap"
          ref={(el: HTMLDivElement) => {
            trackEl = el;
          }}
          onpointerdown={onPointerDown}
          onpointermove={onPointerMove}
          onpointerup={onPointerUp}
          onpointercancel={onPointerUp}
        >
          <div class="bank-verify-captcha-track" />
          <div class="bank-verify-captcha-thumb" style={{ transform: `translateX(${state.thumbX}px)` }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path
                d="M6 7.5l4 3.5-4 3.5M12 7.5l4 3.5-4 3.5"
                stroke="#fff"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
        <div class="bank-verify-captcha-actions">
          <button
            type="button"
            class="bank-verify-captcha-action"
            aria-label="close"
            disabled={locked()}
            onclick={() => close()}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path d="M6.5 6.5l9 9M15.5 6.5l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </button>
          <button
            type="button"
            class="bank-verify-captcha-action"
            aria-label="refresh"
            disabled={!canRefresh()}
            onclick={() => props.onRefresh()}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path d="M16.2 8.2A6 6 0 1 0 17 12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
              <path
                d="M16.2 4.8v3.6H12.6"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
